/**
 * Tiny history router. Item pages and sheets push history entries so the iOS edge
 * back-swipe (and Android back) closes them naturally (SPEC §8.2).
 */
import { useSyncExternalStore } from 'react'
import { moveScreen, skipMove } from './moves'

export type Route =
  | { name: 'home' }
  | { name: 'board' }
  | { name: 'days' }
  | { name: 'notes' }
  | { name: 'pages' }
  | { name: 'search' }
  | { name: 'done' }
  | { name: 'activity' }
  | { name: 'chat' }
  | { name: 'item'; id: string }
  | { name: 'view'; id: string }
  | { name: 'settings'; section: string }
  | { name: 'login' }
  | { name: 'welcome' }
  | { name: 'notfound' }

export interface SheetState {
  kind: string
  data?: unknown
}

export interface Location {
  path: string
  search: URLSearchParams
  route: Route
  sheet: SheetState | null
  key: number
}

export function parse(path: string): Route {
  const p = path.replace(/\/+$/, '') || '/'
  if (p === '/') return { name: 'home' }
  const seg = p.split('/').filter(Boolean)
  switch (seg[0]) {
    case 'home':
    case 'all':
    case 'board':
      return { name: 'board' }
    case 'days':
      return { name: 'days' }
    case 'notes':
      return { name: 'notes' }
    case 'pages':
      return { name: 'pages' }
    case 'search':
      return { name: 'search' }
    case 'done':
      return { name: 'done' }
    case 'activity':
      return { name: 'activity' }
    case 'chat':
      return { name: 'chat' }
    case 'i':
      return seg[1] ? { name: 'item', id: decodeURIComponent(seg[1]) } : { name: 'notfound' }
    case 'v':
      return seg[1] ? { name: 'view', id: decodeURIComponent(seg[1]) } : { name: 'notfound' }
    case 'settings':
      return { name: 'settings', section: seg[1] ?? '' }
    case 'login':
      return { name: 'login' }
    case 'welcome':
      return { name: 'welcome' }
    default:
      return { name: 'notfound' }
  }
}

let key = 0
function read(): Location {
  const st = (history.state ?? {}) as { sheet?: SheetState }
  return {
    path: location.pathname,
    search: new URLSearchParams(location.search),
    route: parse(location.pathname),
    sheet: st.sheet ?? null,
    key: ++key,
  }
}

let current = read()
const subs = new Set<() => void>()
function draw() {
  current = read()
  for (const f of subs) f()
}
function emit() {
  skipMove()
  draw()
}

// Where you were on each screen is kept with its history entry and put back on coming back to
// it (the browser can't: the screen it would scroll is drawn a moment later, as it slides in).
history.scrollRestoration = 'manual'
function keepScroll() {
  history.replaceState({ ...(history.state ?? {}), y: Math.round(scrollY) }, '')
}

/** Show where history now points: a new path moves the screen there (SPEC §7.10), then scrolls to `y`. */
function moveTo(y: number, back = false, uaMoved = false) {
  const from = current.route
  if (location.pathname === current.path) {
    emit()
    if (!back) window.scrollTo({ top: y })
    return
  }
  moveScreen(from, parse(location.pathname), back, draw, () => window.scrollTo({ top: y }), uaMoved)
}

window.addEventListener('popstate', (e) => moveTo((history.state as { y?: number } | null)?.y ?? 0, true, (e as PopStateEvent & { hasUAVisualTransition?: boolean }).hasUAVisualTransition))

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  const url = new URL(to, location.origin)
  if (url.pathname + url.search === location.pathname + location.search && !history.state?.sheet) return
  if (opts.replace) history.replaceState({}, '', url.pathname + url.search)
  else {
    keepScroll()
    history.pushState({}, '', url.pathname + url.search)
  }
  moveTo(0)
}

export function back(fallback = '/') {
  if (history.length > 1 && (history.state as { idx?: number } | null) !== null) history.back()
  else navigate(fallback, { replace: true })
}

export function openSheet(kind: string, data?: unknown) {
  keepScroll()
  history.pushState({ sheet: { kind, data } }, '', location.pathname + location.search)
  emit()
}

/** Swap the pop-up that's open for another (closing it closes both): from a list to one of its things. */
export function swapSheet(kind: string, data?: unknown) {
  if (!(history.state as { sheet?: SheetState } | null)?.sheet) return openSheet(kind, data)
  history.replaceState({ ...(history.state as object), sheet: { kind, data } }, '', location.pathname + location.search)
  emit()
}

export function closeSheet() {
  if ((history.state as { sheet?: SheetState } | null)?.sheet) history.back()
}

/**
 * From inside a sheet, go to a page: the sheet's history entry becomes that page, so Back
 * returns to the page under the sheet. (history.back() + navigate() races popstate.)
 */
export function replaceTo(path: string) {
  const url = new URL(path, location.origin)
  if ((history.state as { sheet?: SheetState } | null)?.sheet) history.replaceState({}, '', url.pathname + url.search)
  else {
    keepScroll()
    history.pushState({}, '', url.pathname + url.search)
  }
  moveTo(0)
}

/** Swap the open sheet for another one without touching history length. */
export function replaceSheet(kind: string, data?: unknown) {
  const st = (history.state ?? {}) as { sheet?: SheetState }
  if (st.sheet) history.replaceState({ sheet: { kind, data } }, '', location.pathname + location.search)
  else {
    keepScroll()
    history.pushState({ sheet: { kind, data } }, '', location.pathname + location.search)
  }
  emit()
}

/** Replace the current sheet's data without adding history. */
export function updateSheet(data: unknown) {
  const st = (history.state ?? {}) as { sheet?: SheetState }
  if (!st.sheet) return
  history.replaceState({ sheet: { ...st.sheet, data } }, '', location.pathname + location.search)
  emit()
}

export function useLocation(): Location {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb)
      return () => subs.delete(cb)
    },
    () => current,
  )
}

export function itemPath(id: string) {
  return `/i/${encodeURIComponent(id)}`
}
